import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { FiArrowLeft, FiX, FiLoader } from 'react-icons/fi'
import { RootState } from '@/store'
import { successStoriesApi } from '@/services/api/successStories'
import { UpdateSuccessStoryData, SuccessStory } from '@/types/successStory'
import LoadingSpinner from '@/components/ui/LoadingSpinner'
import SelectDropdown, { SelectOption } from '@/components/ui/SelectDropdown'
import { useSnackbarContext } from '@/components/ui/SnackbarProvider'
import { extractErrorMessage } from '@/utils/errorHandler'

type SuccessStoryCategoryValue = SuccessStory['category']
type EditSuccessStoryFormData = {
    title: string
    category: SuccessStoryCategoryValue
    achievement: string
    story: string
    testimonial: string
    skills: string[]
}

const EditSuccessStoryPage: React.FC = () => {
    const navigate = useNavigate()
    const { id } = useParams<{ id: string }>()
    const { isAuthenticated } = useSelector((state: RootState) => state.auth)
    const { showSuccess, showError } = useSnackbarContext()

    // Form state
    const [formData, setFormData] = useState<EditSuccessStoryFormData>({
        title: '',
        category: 'technology',
        achievement: '',
        story: '',
        testimonial: '',
        skills: []
    })

    const [skillInput, setSkillInput] = useState('')
    const [loading, setLoading] = useState(true)
    const [submitting, setSubmitting] = useState(false)
    const [skillError, setSkillError] = useState<string>('')
    const [originalStory, setOriginalStory] = useState<SuccessStory | null>(null)

    // Load story data on mount
    useEffect(() => {
        if (!isAuthenticated) {
            navigate('/login')
            return
        }

        if (!id) {
            showError('Story ID is missing')
            navigate('/success-stories')
            return
        }

        const fetchStory = async () => {
            try {
                setLoading(true)
                const response = await successStoriesApi.getUserStoryForEdit(id)
                const story = response.data?.story

                if (!story) {
                    showError('Story not found')
                    navigate('/dashboard/success-story')
                    return
                }

                setOriginalStory(story)
                setFormData({
                    title: story.title || '',
                    category: (story.category || 'technology') as SuccessStoryCategoryValue,
                    achievement: story.achievement || '',
                    story: story.story || '',
                    testimonial: story.testimonial || '',
                    skills: story.skills || []
                })
            } catch (err: any) {
                const message = extractErrorMessage(err, 'Failed to load story')
                showError(message)
                console.error('Error loading story:', err)
                navigate('/dashboard/success-story')
            } finally {
                setLoading(false)
            }
        }

        fetchStory()
    }, [isAuthenticated, navigate, id, showError])

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target
        setFormData(prev => ({
            ...prev,
            [name]: value
        }))
    }

    const handleCategoryChange = (value: string) => {
        setFormData(prev => ({
            ...prev,
            category: value as SuccessStoryCategoryValue
        }))
    }

    const handleAddSkill = () => {
        if (!skillInput.trim()) {
            setSkillError('Please enter a skill')
            return
        }

        if (formData.skills.includes(skillInput.trim())) {
            setSkillError('This skill is already added')
            return
        }

        if (formData.skills.length >= 10) {
            setSkillError('Maximum 10 skills allowed')
            return
        }

        setFormData(prev => ({
            ...prev,
            skills: [...prev.skills, skillInput.trim()]
        }))
        setSkillInput('')
        setSkillError('')
    }

    const handleRemoveSkill = (index: number) => {
        setFormData(prev => ({
            ...prev,
            skills: prev.skills.filter((_, i) => i !== index)
        }))
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        // Validation
        if (!formData.title.trim()) {
            showError('Please enter a title')
            return
        }

        if (formData.title.length < 10) {
            showError('Title must be at least 10 characters')
            return
        }

        if (formData.title.length > 100) {
            showError('Title cannot exceed 100 characters')
            return
        }

        if (!formData.story.trim() || formData.story.length < 50) {
            showError('Story must be at least 50 characters')
            return
        }

        if (formData.story.length > 1000) {
            showError('Story must not exceed 1000 characters')
            return
        }

        if (!formData.testimonial.trim() || formData.testimonial.length < 30) {
            showError('Testimonial must be at least 30 characters')
            return
        }

        if (formData.testimonial.length > 500) {
            showError('Testimonial must not exceed 500 characters')
            return
        }

        if (!formData.achievement.trim() || formData.achievement.length < 10) {
            showError('Achievement must be at least 10 characters')
            return
        }

        if (formData.achievement.length > 100) {
            showError('Achievement must not exceed 100 characters')
            return
        }

        if (formData.skills.length === 0) {
            showError('Please add at least one skill')
            return
        }

        try {
            setSubmitting(true)
            if (!id) {
                showError('Story ID is missing')
                return
            }

            const response = await successStoriesApi.updateSuccessStory(id, formData)

            if (response.data?.story) {
                const successMessage = response.message || 'Success story updated successfully!'
                showSuccess(successMessage)

                // Redirect to my story page
                setTimeout(() => {
                    navigate('/dashboard/success-story')
                }, 1500)
            }
        } catch (err: any) {
            const message = extractErrorMessage(err, 'Failed to update story')
            showError(message)
            console.error('Error updating story:', err)
        } finally {
            setSubmitting(false)
        }
    }

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
                <LoadingSpinner />
            </div>
        )
    }

    if (!originalStory) {
        return (
            <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
                    <button
                        onClick={() => navigate('/dashboard/success-story')}
                        className="flex items-center text-primary-600 hover:text-primary-700 mb-8 font-medium text-sm sm:text-base"
                    >
                        <FiArrowLeft className="w-4 h-4 mr-2" />
                        Back to My Story
                    </button>
                    <div className="bg-white dark:bg-gray-800 rounded-xl p-4 sm:p-6 md:p-8 text-center">
                        <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-2">Story Not Found</h2>
                        <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
                            The success story you're trying to edit could not be found.
                        </p>
                    </div>
                </div>
            </div>
        )
    }

    const storyLength = formData.story.length
    const testimonialLength = formData.testimonial.length
    const achievementLength = formData.achievement.length

    const categoryOptions: SelectOption[] = [
        { value: 'technology', label: 'Technology' },
        { value: 'creative', label: 'Creative' },
        { value: 'marketing', label: 'Marketing' },
        { value: 'other', label: 'Other' }
    ]

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
                {/* Header */}
                <div className="mb-8">
                    <button
                        onClick={() => navigate('/dashboard/success-story')}
                        className="flex items-center text-primary-600 hover:text-primary-700 mb-4 font-medium text-sm sm:text-base"
                    >
                        <FiArrowLeft className="w-4 h-4 mr-2" />
                        Back to My Story
                    </button>
                    <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 dark:text-white mb-2">Edit Your Success Story</h1>
                    <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
                        Update your story. It will be reviewed by our team again before re-publication.
                    </p>
                </div>

                {/* Status Alert */}
                {originalStory.status === 'rejected' && (
                    <div className="mb-6 p-4 bg-red-50 dark:bg-red-900 border-l-4 border-red-400 rounded">
                        <p className="text-sm sm:text-base text-red-800 dark:text-red-200 mb-2">
                            ❌ Your previous submission was rejected.
                        </p>
                        {originalStory.rejectionReason && (
                            <p className="text-sm sm:text-base text-red-800 dark:text-red-200">
                                <strong>Reason:</strong> {originalStory.rejectionReason}
                            </p>
                        )}
                        <p className="text-sm sm:text-base text-red-800 dark:text-red-200 mt-2">
                            Please address the issue and resubmit.
                        </p>
                    </div>
                )}

                {originalStory.status === 'pending' && (
                    <div className="mb-6 p-4 bg-yellow-50 dark:bg-yellow-900 border-l-4 border-yellow-400 rounded">
                        <p className="text-sm sm:text-base text-yellow-800 dark:text-yellow-200">
                            ⏳ Your story is currently pending review. You can still make changes.
                        </p>
                    </div>
                )}

                {originalStory.status === 'approved' && (
                    <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900 border-l-4 border-blue-400 rounded">
                        <p className="text-sm sm:text-base text-blue-800 dark:text-blue-200 mb-2">
                            ℹ️ Your story is currently published. When you save changes, your story will be temporarily unpublished and sent for review.
                        </p>
                        <p className="text-sm sm:text-base text-blue-800 dark:text-blue-200 mt-2">
                            Once our team reviews the changes, your story will be published again.
                        </p>
                    </div>
                )}

                {/* Form */}
                <form onSubmit={handleSubmit} className="bg-white dark:bg-gray-800 rounded-xl p-4 sm:p-6 md:p-8 shadow-md">
                    {/* Title */}
                    <div className="mb-6">
                        <label className="block text-sm sm:text-base font-medium text-gray-900 dark:text-white mb-2">
                            Story Title *
                        </label>
                        <input
                            type="text"
                            name="title"
                            value={formData.title}
                            onChange={handleInputChange}
                            maxLength={100}
                            placeholder="e.g., Built a 6-figure freelance business"
                            className="w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                        />
                        <div className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-2">
                            {formData.title.length}/100
                        </div>
                    </div>

                    {/* Category */}
                    <div className="mb-6">
                        <SelectDropdown
                            value={formData.category || 'technology'}
                            onChange={handleCategoryChange}
                            options={categoryOptions}
                            label="Category *"
                            className="w-full"
                        />
                    </div>

                    {/* Achievement */}
                    <div className="mb-6">
                        <label className="block text-sm sm:text-base font-medium text-gray-900 dark:text-white mb-2">
                            Key Achievement *
                        </label>
                        <input
                            type="text"
                            name="achievement"
                            value={formData.achievement}
                            onChange={handleInputChange}
                            maxLength={100}
                            placeholder="e.g., Launched 15+ successful mobile apps"
                            className="w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                        />
                        <div className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-2">
                            {achievementLength}/100
                        </div>
                    </div>

                    {/* Your Story */}
                    <div className="mb-6">
                        <label className="block text-sm sm:text-base font-medium text-gray-900 dark:text-white mb-2">
                            Your Story *
                        </label>
                        <textarea
                            name="story"
                            value={formData.story}
                            onChange={handleInputChange}
                            maxLength={1000}
                            rows={6}
                            placeholder="Tell us your story... What was your starting point? What challenges did you face? How did SideGO help? What's your current situation?"
                            className="w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 resize-vertical"
                        />
                        <div className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-2">
                            {storyLength}/1000 characters
                        </div>
                    </div>

                    {/* Testimonial */}
                    <div className="mb-6">
                        <label className="block text-sm sm:text-base font-medium text-gray-900 dark:text-white mb-2">
                            Testimonial / Quote *
                        </label>
                        <textarea
                            name="testimonial"
                            value={formData.testimonial}
                            onChange={handleInputChange}
                            maxLength={500}
                            rows={4}
                            placeholder='e.g., "SideGO gave me the platform to showcase my skills and connect with amazing clients."'
                            className="w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 resize-vertical"
                        />
                        <div className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-2">
                            {testimonialLength}/500 characters
                        </div>
                    </div>

                    {/* Skills */}
                    <div className="mb-8">
                        <label className="block text-sm sm:text-base font-medium text-gray-900 dark:text-white mb-2">
                            Key Skills * (minimum 1, maximum 10)
                        </label>
                        <div className="flex flex-col sm:flex-row gap-2 mb-4">
                            <input
                                type="text"
                                value={skillInput}
                                onChange={(e) => {
                                    setSkillInput(e.target.value)
                                    setSkillError('')
                                }}
                                onKeyPress={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault()
                                        handleAddSkill()
                                    }
                                }}
                                placeholder="e.g., React, Node.js, Python"
                                className="flex-1 px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                            />
                            <button
                                type="button"
                                onClick={handleAddSkill}
                                className="px-4 sm:px-6 py-2 sm:py-3 bg-primary-600 hover:bg-primary-700 text-white font-medium text-sm sm:text-base rounded-lg transition-colors whitespace-nowrap"
                            >
                                Add
                            </button>
                        </div>
                        {skillError && (
                            <p className="text-sm sm:text-base text-red-600 dark:text-red-400 mb-3">{skillError}</p>
                        )}
                        <div className="flex flex-wrap gap-2">
                            {formData.skills.map((skill, index) => (
                                <span
                                    key={index}
                                    className="px-2 sm:px-3 py-1.5 sm:py-2 bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-300 text-xs sm:text-sm rounded-full flex items-center gap-2"
                                >
                                    {skill}
                                    <button
                                        type="button"
                                        onClick={() => handleRemoveSkill(index)}
                                        className="hover:text-primary-900 dark:hover:text-primary-100"
                                    >
                                        <FiX className="w-4 h-4" />
                                    </button>
                                </span>
                            ))}
                        </div>
                    </div>

                    {/* Submit Button */}
                    <div className="flex flex-col sm:flex-row gap-3">
                        <button
                            type="submit"
                            disabled={submitting}
                            className="flex-1 px-4 sm:px-6 py-2 sm:py-3 bg-primary-600 hover:bg-primary-700 text-white font-medium text-sm sm:text-base rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                        >
                            {submitting ? (
                                <>
                                    <FiLoader className="w-4 h-4 animate-spin" />
                                    Updating...
                                </>
                            ) : (
                                'Update Story'
                            )}
                        </button>
                        <button
                            type="button"
                            onClick={() => navigate('/dashboard/success-story')}
                            className="px-4 sm:px-6 py-2 sm:py-3 border-2 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white font-medium text-sm sm:text-base rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                        >
                            Cancel
                        </button>
                    </div>
                </form>
            </div>
        </div>
    )
}

export default EditSuccessStoryPage